import React, { useEffect } from 'react';
import { Layers, Zap, Database, ShieldCheck, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';

export const Corporate = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20">
      
      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden flex items-center min-h-[80vh]">
        <div className="absolute inset-0 z-0">
           <img 
            src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=2000" 
            className="w-full h-full object-cover opacity-30"
            alt="Corporate Event"
           />
           <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full bg-brand-neonBlue/10 border border-brand-neonBlue text-brand-neonBlue text-xs font-bold uppercase tracking-wider">
              <Zap size={14} /> Experiential Marketing
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 leading-tight">
              Turn Attendees into <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-neonBlue to-brand-neonPink">Brand Ambassadors</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-xl">
              Don't just entertain. Capture data, generate millions of social impressions, and dominate the feed with QuikPix corporate activations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" onClick={onOpenQuote}>Start Your Activation</Button>
              <Button variant="outline" size="lg">Download Media Kit</Button>
            </div>
          </div>

          {/* Abstract Analytics Visual */}
          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
             <GlassCard className="border-brand-neonBlue/30 bg-slate-900/80 backdrop-blur-xl relative z-10">
                <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
                  <div>
                    <h3 className="text-white font-bold">Post-Event Impact Report</h3>
                    <p className="text-xs text-slate-400">Campaign: Summer Launch</p>
                  </div>
                  <div className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded">Live Data</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="p-4 rounded-lg bg-white/5">
                    <div className="text-slate-400 text-xs uppercase mb-1">Total Reach</div>
                    <div className="text-2xl font-display font-bold text-white">1.2M+</div>
                    <div className="text-xs text-green-400 flex items-center mt-1"><TrendingUp size={12} className="mr-1"/> +24% vs goal</div>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5">
                    <div className="text-slate-400 text-xs uppercase mb-1">Data Captured</div>
                    <div className="text-2xl font-display font-bold text-brand-neonBlue">854</div>
                    <div className="text-xs text-slate-500 mt-1">Emails & Phones</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm text-slate-300">
                    <span>Instagram Stories</span>
                    <span className="font-bold">45%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 w-[45%]"></div>
                  </div>
                  
                  <div className="flex justify-between text-sm text-slate-300 pt-2">
                    <span>TikTok Shares</span>
                    <span className="font-bold">32%</span>
                  </div>
                  <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-brand-neonBlue to-cyan-500 w-[32%]"></div>
                  </div>
                </div>
             </GlassCard>
             
             {/* Decorative Elements */}
             <div className="absolute -top-10 -right-10 w-40 h-40 bg-brand-neonPink/20 rounded-full blur-[50px]"></div>
             <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-brand-neonBlue/20 rounded-full blur-[50px]"></div>
          </div>
        </div>
      </section>

      {/* The Corporate Difference */}
      <section className="py-20 bg-slate-900 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-display font-bold text-white mb-4">Built for Agency Standards</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We understand that your client's brand reputation is on the line. Our corporate infrastructure is designed for reliability, compliance, and speed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <GlassCard className="p-8 hover:border-brand-neonBlue/50 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-brand-neonBlue/10 flex items-center justify-center text-brand-neonBlue mb-6">
                <Database size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">GDPR Compliant Data</h3>
              <p className="text-slate-400 leading-relaxed">
                Securely capture emails, phone numbers, and survey answers. All data is encrypted and delivered in CSV format immediately post-event for your CRM.
              </p>
            </GlassCard>

            <GlassCard className="p-8 hover:border-brand-neonPink/50 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-brand-neonPink/10 flex items-center justify-center text-brand-neonPink mb-6">
                <Layers size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Total Brand Immersion</h3>
              <p className="text-slate-400 leading-relaxed">
                From the vinyl wrap on the hardware to the custom microsite where guests download photos. Every touchpoint features your logo, not ours.
              </p>
            </GlassCard>

            <GlassCard className="p-8 hover:border-white/30 transition-colors">
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center text-white mb-6">
                <ShieldCheck size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Health & Safety Rated</h3>
              <p className="text-slate-400 leading-relaxed">
                We carry $10M Public Liability Insurance. All equipment is tagged and tested. RAMS forms provided for every venue upon request.
              </p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Hardware Branding Showcase */}
      <section className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
           <div className="order-2 lg:order-1 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-brand-neonBlue/20 to-brand-neonPink/20 blur-[100px]"></div>
              <img 
                src="https://images.unsplash.com/photo-1561489413-985b06da5bee?auto=format&fit=crop&q=80&w=1200" 
                alt="Branded Booth Example" 
                className="relative z-10 rounded-2xl border border-white/10 shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 z-20 bg-slate-900 border border-white/10 p-6 rounded-xl shadow-xl max-w-xs">
                 <div className="text-xs text-slate-500 uppercase font-bold mb-2">Customization Options</div>
                 <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-white"><CheckCircle size={14} className="text-brand-neonBlue"/> Full Vinyl Wrap</li>
                    <li className="flex items-center gap-2 text-sm text-white"><CheckCircle size={14} className="text-brand-neonBlue"/> Custom Attendant Uniforms</li>
                    <li className="flex items-center gap-2 text-sm text-white"><CheckCircle size={14} className="text-brand-neonBlue"/> Branded Microsite URL</li>
                 </ul>
              </div>
           </div>
           
           <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-6">Your Brand,<br /> Front & Center.</h2>
              <p className="text-lg text-slate-300 mb-6">
                We are the ghostwriters of the event world. We fade into the background so your brand shines.
              </p>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Our "White Label" service means the physical booth becomes a billboard for your campaign. Whether it's a 360 spinner or a roaming photographer, the guest experience is 100% yours.
              </p>
              
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-4">
                   <div className="p-2 bg-slate-800 rounded-lg text-white font-bold text-xl">01</div>
                   <div>
                     <h4 className="text-white font-bold">Concept & Mockups</h4>
                     <p className="text-sm text-slate-400">Send us your assets, we provide 3D renders of the booth in your branding within 24 hours.</p>
                   </div>
                </div>
                <div className="flex items-start gap-4">
                   <div className="p-2 bg-slate-800 rounded-lg text-white font-bold text-xl">02</div>
                   <div>
                     <h4 className="text-white font-bold">Microsite Development</h4>
                     <p className="text-sm text-slate-400">We build a tracking pixel-enabled landing page for photo retrieval.</p>
                   </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                 <Button onClick={onOpenQuote}>Request Branding Specs</Button>
              </div>
           </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-950 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-display font-bold text-white mb-6">Planning a Multi-City Roadshow?</h2>
          <p className="text-lg text-slate-300 mb-8">
            We have logistics hubs in Auckland, Wellington, and Christchurch. One contract covers the entire nation.
          </p>
          <Button size="lg" variant="secondary" onClick={onOpenQuote}>
            Contact Corporate Accounts Team
          </Button>
        </div>
      </section>

    </div>
  );
};

// Helper component for checkmarks
const CheckCircle = ({ size, className }: { size: number, className: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);
